/**
 * Platform Fees admin section — global fees CHARGED TO PooPartners,
 * separate from the per-tier platform fee. Consumed by the tier's
 * Example Payout panel (section E) when `enabled === true`.
 *
 * Per-card edit mode mirrors the tier/plan sections in `AdminTaxonomySections.jsx`.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Loader2, DollarSign, Plus, Trash2, GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import BadgeChip from "@/components/BadgeChip";

const BASIS_LABEL = {
  per_booking_pct: "Per booking",
  per_booking_flat: "Per booking",
  monthly_flat: "Monthly",
};
const BASIS_AFFIX = { per_booking_pct: "percent", per_booking_flat: "flat", monthly_flat: "flat" };

function validate(name, basis, value) {
  const errs = {};
  if (!String(name || "").trim()) errs.name = "Required.";
  else if (String(name).trim().length > 30) errs.name = "30 characters max.";
  if (!basis) errs.basis = "Pick a basis.";
  const n = Number(value);
  if (value === "" || !Number.isFinite(n)) errs.value = "Number required.";
  else if (n < 0) errs.value = "Must be 0 or more.";
  else if (basis === "per_booking_pct" && n > 100) errs.value = "Must be 0–100.";
  return errs;
}

function FeeChip({ basis, value, enabled }) {
  if (basis === "per_booking_pct") {
    return (
      <span className="inline-flex items-center rounded-full bg-pp-surface2 px-3 py-1 text-xs font-semibold text-pp-text">
        {BASIS_LABEL[basis]} · {Number(value).toFixed(1)}%
        {!enabled && <span className="ml-2 text-pp-text-muted">(disabled)</span>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-pp-surface2 px-3 py-1 text-xs font-semibold text-pp-text">
      {BASIS_LABEL[basis]} · ${Number(value).toFixed(2)}
      {!enabled && <span className="ml-2 text-pp-text-muted">(disabled)</span>}
    </span>
  );
}

function FeeCard({ row, tiers, onSave, onDelete, saving, dragHandleProps }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(row.name);
  const [basis, setBasis] = useState(row.basis);
  const [value, setValue] = useState(String(row.value));
  const [enabled, setEnabled] = useState(!!row.enabled);
  // Sort order is now driven exclusively by drag-and-drop. Numeric input
  // was removed (Admin Portal Consolidation Step 2). The row's
  // `sort_order` still rides along on the doc for read-after-reorder,
  // but is never user-editable as a number.
  const [appliesTo, setAppliesTo] = useState(() => row.applies_to_tier_ids || []);

  // Reset draft when canonical doc changes (post-save).
  useEffect(() => {
    setName(row.name);
    setBasis(row.basis);
    setValue(String(row.value));
    setEnabled(!!row.enabled);
    setAppliesTo(row.applies_to_tier_ids || []);
  }, [row]);

  // Esc cancels edit mode.
  useEffect(() => {
    if (!editing) return;
    const h = (e) => {
      if (e.key === "Escape") cancel();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // `cancel` is a local function that closes over draft-clearing
    // setters (stable); adding it would re-add/remove the keydown
    // listener on every render. Same shape as AdminTaxonomySections:492.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const errors = useMemo(() => validate(name, basis, value), [name, basis, value]);
  const hasErrors = Object.keys(errors).length > 0;
  // applies_to_tier_ids order-independent diff — admin reordering tiers
  // shouldn't show the fee as dirty when nothing semantic changed.
  const appliesToChanged = useMemo(() => {
    const a = new Set(appliesTo);
    const b = new Set(row.applies_to_tier_ids || []);
    if (a.size !== b.size) return true;
    for (const v of a) if (!b.has(v)) return true;
    return false;
  }, [appliesTo, row.applies_to_tier_ids]);

  const dirty =
    name.trim() !== row.name ||
    basis !== row.basis ||
    Number(value) !== Number(row.value) ||
    enabled !== !!row.enabled ||
    appliesToChanged;

  const cancel = () => {
    setName(row.name);
    setBasis(row.basis);
    setValue(String(row.value));
    setEnabled(!!row.enabled);
    setAppliesTo(row.applies_to_tier_ids || []);
    setEditing(false);
  };

  const submit = async () => {
    if (hasErrors || !dirty) return;
    const payload = {};
    if (name.trim() !== row.name) payload.name = name.trim();
    if (basis !== row.basis) payload.basis = basis;
    if (Number(value) !== Number(row.value)) payload.value = Number(value);
    if (enabled !== !!row.enabled) payload.enabled = enabled;
    if (appliesToChanged) payload.applies_to_tier_ids = appliesTo;
    await onSave(row.id, payload);
    setEditing(false);
  };

  const toggleTier = (tierId) => {
    setAppliesTo((prev) =>
      prev.includes(tierId) ? prev.filter((t) => t !== tierId) : [...prev, tierId]
    );
  };

  // "Applies to: …" summary for the read-only view. Three states:
  //   * applies to every current tier → "all tiers"
  //   * empty list                    → "no tiers"
  //   * partial                       → comma-list of tier names
  const appliesSummary = useMemo(() => {
    if (!tiers || tiers.length === 0) return null;
    const allIds = new Set(tiers.map((t) => t.id));
    const fromRow = row.applies_to_tier_ids || [];
    if (fromRow.length === 0) return { label: "no tiers", muted: true };
    if (allIds.size === fromRow.length && fromRow.every((id) => allIds.has(id))) {
      return { label: "all tiers", muted: false };
    }
    const names = tiers
      .filter((t) => fromRow.includes(t.id))
      .map((t) => t.name);
    return { label: names.join(", "), muted: false };
  }, [tiers, row.applies_to_tier_ids]);

  return (
    <Card
      className="rounded-2xl border border-pp-border bg-pp-surface p-6"
      style={{ boxShadow: "var(--pp-card-shadow)" }}
      data-testid={`platform-fee-${row.id}-card`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {dragHandleProps !== undefined && (
            <button
              type="button"
              {...(dragHandleProps || {})}
              className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-pp-text-muted transition-colors hover:bg-pp-surface2 hover:text-pp-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Drag to reorder"
              disabled={editing}
              data-testid={`platform-fee-${row.id}-drag-handle`}
            >
              <GripVertical className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <div className="min-w-0">
            <h3 className="font-heading text-xl font-bold text-pp-text" data-testid={`platform-fee-${row.id}-name`}>
              {row.name}
            </h3>
            {!editing && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <FeeChip basis={row.basis} value={row.value} enabled={row.enabled} />
                {appliesSummary && (
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full bg-pp-surface2 px-3 py-1 text-xs font-semibold",
                      appliesSummary.muted ? "text-pp-text-muted" : "text-pp-text"
                    )}
                    data-testid={`platform-fee-${row.id}-applies-to`}
                  >
                    Applies to: {appliesSummary.label}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Enable toggle is always visible — primary UX control. */}
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            {editing ? (enabled ? "On" : "Off") : (row.enabled ? "On" : "Off")}
          </span>
          <Switch
            checked={editing ? enabled : !!row.enabled}
            onCheckedChange={(v) => {
              if (!editing) {
                // Quick-toggle from read-only mode: save immediately.
                onSave(row.id, { enabled: !!v });
              } else {
                setEnabled(!!v);
              }
            }}
            data-testid={`platform-fee-${row.id}-enabled`}
          />
        </div>
      </div>

      {editing && (
        <>
          <hr className="my-5 border-pp-border" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={cn("mt-2 h-11 rounded-full px-5", errors.name && "border-red-500")}
                data-testid={`platform-fee-${row.id}-name-input`}
              />
              {errors.name && <p className="mt-1 text-xs font-semibold text-red-600">{errors.name}</p>}
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Basis</Label>
              <Select value={basis} onValueChange={setBasis}>
                <SelectTrigger className="mt-2 h-11 rounded-full" data-testid={`platform-fee-${row.id}-basis-trigger`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_booking_pct">Per booking — %</SelectItem>
                  <SelectItem value="per_booking_flat">Per booking — flat $</SelectItem>
                  <SelectItem value="monthly_flat">Monthly — flat $</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Value</Label>
              <div className="relative mt-2">
                {BASIS_AFFIX[basis] === "flat" && (
                  <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">$</span>
                )}
                <Input
                  type="number"
                  step="0.01"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className={cn(
                    "h-11 rounded-full px-5",
                    BASIS_AFFIX[basis] === "flat" && "pl-9",
                    BASIS_AFFIX[basis] === "percent" && "pr-9",
                    errors.value && "border-red-500"
                  )}
                  data-testid={`platform-fee-${row.id}-value-input`}
                />
                {BASIS_AFFIX[basis] === "percent" && (
                  <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">%</span>
                )}
              </div>
              {errors.value && <p className="mt-1 text-xs font-semibold text-red-600">{errors.value}</p>}
            </div>
            {/* Per-tier toggle grid — one checkbox per tier so admins can
                opt the fee out of specific tiers without disabling the
                fee globally. Edit-mode only; the read-only summary above
                shows the resulting set. */}
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                Applies to tiers
              </Label>
              <div
                className="mt-2 flex flex-wrap gap-2"
                data-testid={`platform-fee-${row.id}-applies-to-edit`}
              >
                {(tiers || []).map((t) => {
                  const checked = appliesTo.includes(t.id);
                  return (
                    <label
                      key={t.id}
                      className={cn(
                        "inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition-colors",
                        checked
                          ? "border-brand-orange bg-brand-orange/10 text-pp-text"
                          : "border-pp-border bg-pp-surface text-pp-text-muted hover:bg-pp-surface2"
                      )}
                      data-testid={`platform-fee-${row.id}-tier-${t.id}-label`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTier(t.id)}
                        className="h-4 w-4 accent-brand-orange"
                        data-testid={`platform-fee-${row.id}-tier-${t.id}-checkbox`}
                      />
                      {t.badge_id && t.badge_id !== "none" && (
                        <BadgeChip badgeId={t.badge_id} size="xs" />
                      )}
                      <span>{t.name}</span>
                    </label>
                  );
                })}
                {(!tiers || tiers.length === 0) && (
                  <span className="text-xs text-pp-text-muted">No tiers configured.</span>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        {editing ? (
          <>
            <Button
              variant="ghost"
              type="button"
              onClick={cancel}
              className="rounded-full"
              data-testid={`platform-fee-${row.id}-cancel`}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={!dirty || hasErrors || saving}
              className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
              data-testid={`platform-fee-${row.id}-save`}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onDelete(row)}
              className="rounded-full text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid={`platform-fee-${row.id}-delete`}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
            <Button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
              data-testid={`platform-fee-${row.id}-edit`}
            >
              Edit
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}

function AddFeeDialog({ open, onOpenChange, onSubmit }) {
  const [name, setName] = useState("");
  const [basis, setBasis] = useState("per_booking_pct");
  const [value, setValue] = useState("0");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setBasis("per_booking_pct");
      setValue("0");
    }
  }, [open]);

  const errors = useMemo(() => validate(name, basis, value), [name, basis, value]);
  const hasErrors = Object.keys(errors).length > 0;

  const submit = async () => {
    if (hasErrors) return;
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), basis, value: Number(value) });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-pp-surface" data-testid="platform-fee-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-pp-text">New platform fee</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={cn("mt-2 h-11 rounded-full px-5", errors.name && "border-red-500")}
              data-testid="platform-fee-add-name-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Basis</Label>
            <Select value={basis} onValueChange={setBasis}>
              <SelectTrigger className="mt-2 h-11 rounded-full" data-testid="platform-fee-add-basis-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="per_booking_pct">Per booking — %</SelectItem>
                <SelectItem value="per_booking_flat">Per booking — flat $</SelectItem>
                <SelectItem value="monthly_flat">Monthly — flat $</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Value</Label>
            <div className="relative mt-2">
              {BASIS_AFFIX[basis] === "flat" && (
                <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">$</span>
              )}
              <Input
                type="number"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={cn(
                  "h-11 rounded-full px-5",
                  BASIS_AFFIX[basis] === "flat" && "pl-9",
                  BASIS_AFFIX[basis] === "percent" && "pr-9",
                  errors.value && "border-red-500"
                )}
                data-testid="platform-fee-add-value-input"
              />
              {BASIS_AFFIX[basis] === "percent" && (
                <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">%</span>
              )}
            </div>
            {errors.value && <p className="mt-1 text-xs font-semibold text-red-600">{errors.value}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={submitting || hasErrors}
            onClick={submit}
            className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
            data-testid="platform-fee-add-submit"
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PlatformFeesSection() {
  const [rows, setRows] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = async () => {
    try {
      // Fetch fees + tiers in parallel — the fee cards need both to
      // render the "Applies to" summary and the per-tier checkbox grid.
      const [feesRes, tiersRes] = await Promise.all([
        api.get("/admin/platform-fees"),
        api.get("/admin/partner-tiers"),
      ]);
      setRows(feesRes.data);
      setTiers(tiersRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const save = async (id, payload) => {
    setSavingId(id);
    try {
      const { data } = await api.put(`/admin/platform-fees/${id}`, payload);
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success("Fee updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingId(null);
    }
  };

  const submitAdd = async (body) => {
    try {
      const { data } = await api.post("/admin/platform-fees", body);
      setRows((rs) => [...rs, data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      toast.success("Fee created.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/platform-fees/${deleteTarget.id}`);
      setRows((rs) => rs.filter((r) => r.id !== deleteTarget.id));
      toast.success("Fee deleted.");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // ---- DnD reorder (Admin Portal Consolidation Step 2) ----------------
  // Optimistic local reorder, then PUT the new order array. Server is the
  // source of truth — its 200 response is the canonical post-reorder
  // sequence, so we drop the local optimistic state on its head. On
  // failure we restore the prior `rows` snapshot and toast.
  const [reorderBusy, setReorderBusy] = useState(false);
  const onDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    if (reorderBusy) return;
    const snapshot = rows;
    const next = Array.from(rows);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setRows(next);
    setReorderBusy(true);
    try {
      const { data } = await api.put("/admin/platform-fees/reorder", {
        order: next.map((r) => r.id),
      });
      setRows(data);
    } catch (e) {
      setRows(snapshot);
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Couldn't save the new order. Please try again."
      );
    } finally {
      setReorderBusy(false);
    }
  };

  return (
    <section className="mt-2" data-testid="platform-fees-section">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-brand-orange" aria-hidden="true" />
            <h2 className="font-heading text-2xl font-semibold text-pp-text">Platform Fees</h2>
          </div>
          <p className="mt-1 text-sm text-pp-text-muted">
            Global fees charged to PooPartners, separate from the per-tier platform fee.
            Per-booking fees factor into each tier&rsquo;s example payout.
            Drag rows to reorder.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setAddOpen(true)}
          className="h-11 rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid="platform-fee-add-button"
        >
          <Plus className="mr-2 h-4 w-4" /> Add fee
        </Button>
      </header>

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-pp-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="platform-fees-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-4"
                aria-busy={reorderBusy}
                data-testid="platform-fees-list"
              >
                {rows.map((r, idx) => (
                  <Draggable key={r.id} draggableId={r.id} index={idx}>
                    {(dProvided, snap) => (
                      <div
                        ref={dProvided.innerRef}
                        {...dProvided.draggableProps}
                        style={{
                          ...dProvided.draggableProps.style,
                          opacity: snap.isDragging ? 0.92 : 1,
                        }}
                      >
                        <FeeCard
                          row={r}
                          tiers={tiers}
                          onSave={save}
                          onDelete={setDeleteTarget}
                          saving={savingId === r.id}
                          dragHandleProps={dProvided.dragHandleProps}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <AddFeeDialog open={addOpen} onOpenChange={setAddOpen} onSubmit={submitAdd} />

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="rounded-3xl bg-pp-surface">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-pp-text">
              Delete &ldquo;{deleteTarget?.name}&rdquo;?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-pp-text-muted">This cannot be undone.</p>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              className="rounded-full bg-red-600 text-white hover:bg-red-700"
              data-testid="platform-fee-delete-confirm"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
